import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { adminApi } from '../api';
import { Heart, MessageSquare, Image as ImageIcon, TrendingUp, Loader2 } from 'lucide-react';

const GalleryStatsDialog = ({ galleryId, onClose }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('top');

  useEffect(() => {
    (async () => {
      try { const s = await adminApi.galleryStats(galleryId); setData(s); }
      catch { onClose(); }
    })();
  }, [galleryId, onClose]);

  return (
    <Dialog open={true} onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#B8944B]" /> Statistiche lavoro</DialogTitle>
          <DialogDescription>{data ? `${data.gallery.title} · ${data.total_photos} foto · ${data.total_likes} like · ${data.total_comments} commenti` : 'Caricamento statistiche...'}</DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="py-12 flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                <ImageIcon className="w-4 h-4 text-[#B8944B] mx-auto mb-1" />
                <div className="text-lg font-bold">{data.total_photos}</div>
                <div className="text-[10px] uppercase text-slate-500">Foto</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                <Heart className="w-4 h-4 text-rose-500 mx-auto mb-1" />
                <div className="text-lg font-bold">{data.total_likes}</div>
                <div className="text-[10px] uppercase text-slate-500">Like</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                <MessageSquare className="w-4 h-4 text-[#B8944B] mx-auto mb-1" />
                <div className="text-lg font-bold">{data.total_comments}</div>
                <div className="text-[10px] uppercase text-slate-500">Commenti</div>
              </div>
            </div>

            <div className="flex items-center gap-2 border-b border-slate-200 mt-4">
              <button onClick={()=>setTab('top')} className={`px-3 py-2 text-xs font-medium border-b-2 transition ${tab==='top' ? 'border-[#B8944B] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>Top 10 foto più amate</button>
              <button onClick={()=>setTab('comments')} className={`px-3 py-2 text-xs font-medium border-b-2 transition ${tab==='comments' ? 'border-[#B8944B] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>Commenti ({data.total_comments})</button>
            </div>

            {tab === 'top' && (
              <div className="space-y-2 mt-3">
                {data.top_liked.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Nessun like ancora.</p>}
                {data.top_liked.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-2 border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-[#C9A961]/20 text-[#8A7434] grid place-items-center font-bold text-sm">{i+1}</div>
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-black shrink-0">
                      <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.filename}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Amata da: {p.likes_by.join(', ') || '—'}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="inline-flex items-center gap-1 text-xs bg-rose-50 text-rose-600 border border-rose-200 px-2 py-1 rounded-full font-semibold"><Heart className="w-3 h-3 fill-current" /> {p.likes}</span>
                      {p.comment_count > 0 && <span className="inline-flex items-center gap-1 text-xs bg-[#F7F1E1] text-[#8A7434] border border-[#C9A961]/30 px-2 py-1 rounded-full font-semibold"><MessageSquare className="w-3 h-3" /> {p.comment_count}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'comments' && (
              <div className="space-y-3 mt-3">
                {data.photos_with_comments.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Nessun commento ancora.</p>}
                {data.photos_with_comments.map(p => (
                  <div key={p.id} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-black shrink-0">
                        <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{p.filename}</div>
                        <div className="text-xs text-slate-500">{p.comments.length} commenti</div>
                      </div>
                    </div>
                    <div className="space-y-1.5 ml-15">
                      {p.comments.map(c => (
                        <div key={c.id} className="bg-white rounded-lg p-2 border border-slate-100">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-semibold text-[#8A7434]">{c.author}</span>
                            <span className="text-[10px] text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString('it-IT', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'}) : ''}</span>
                          </div>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GalleryStatsDialog;
